import { useState, useRef } from 'react';
import { supabase } from '../../../../lib/supabase';

interface Props {
  value: string;
  onChange: (url: string) => void;
}

const BUCKET = 'product-images';

export default function ImageUploader({ value, onChange }: Props) {
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState<'upload' | 'url'>('upload');
  const [urlInput, setUrlInput] = useState(value.startsWith('http') ? value : '');
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('請上傳圖片檔案（JPG、PNG、WebP 等）');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('圖片檔案大小不可超過 5MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const ext = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(fileName, file, { upsert: false });

      if (uploadError) {
        if (uploadError.message.includes('Bucket not found')) {
          setError('請先在 Supabase Dashboard > Storage 建立名為「product-images」的 bucket，並設為 Public。');
        } else {
          setError(`上傳失敗：${uploadError.message}`);
        }
        setUploading(false);
        return;
      }

      const { data } = supabase.storage.from(BUCKET).getPublicUrl(fileName);
      onChange(data.publicUrl);
    } catch {
      setError('上傳時發生錯誤，請稍後再試。');
    }
    setUploading(false);
  };

  const handleUrlApply = () => {
    if (!urlInput.trim()) return;
    onChange(urlInput.trim());
  };

  return (
    <div>
      {/* Tab */}
      <div className="flex gap-0 mb-3 border border-gray-200 w-fit">
        <button
          type="button"
          onClick={() => setTab('upload')}
          className={`px-3 py-1.5 text-xs transition-colors cursor-pointer whitespace-nowrap ${
            tab === 'upload' ? 'bg-[#1a2535] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
          }`}
        >
          <i className="ri-upload-line mr-1" />
          上傳圖片
        </button>
        <button
          type="button"
          onClick={() => setTab('url')}
          className={`px-3 py-1.5 text-xs transition-colors cursor-pointer whitespace-nowrap ${
            tab === 'url' ? 'bg-[#1a2535] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'
          }`}
        >
          <i className="ri-links-line mr-1" />
          圖片網址
        </button>
      </div>

      {tab === 'upload' && (
        <div>
          <div
            onClick={() => !uploading && fileRef.current?.click()}
            className={`border-2 border-dashed border-gray-200 p-5 text-center transition-colors ${
              uploading ? 'cursor-wait bg-gray-50' : 'cursor-pointer hover:border-[#1a73b5] hover:bg-[#f0f6ff]'
            }`}
          >
            {uploading ? (
              <div className="flex items-center justify-center gap-2 text-gray-400">
                <i className="ri-loader-4-line animate-spin" />
                <span className="text-xs">上傳中...</span>
              </div>
            ) : (
              <>
                <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center">
                  <i className="ri-image-add-line text-2xl text-gray-300" />
                </div>
                <p className="text-xs text-gray-400">點擊選擇圖片，或拖曳至此</p>
                <p className="text-[10px] text-gray-300 mt-0.5">支援 JPG、PNG、WebP，最大 5MB</p>
              </>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      )}

      {tab === 'url' && (
        <div className="flex gap-2">
          <input
            type="url"
            value={urlInput}
            onChange={e => setUrlInput(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="flex-1 text-xs border border-gray-200 px-3 py-2 focus:outline-none focus:border-[#1a73b5]"
          />
          <button
            type="button"
            onClick={handleUrlApply}
            className="px-3 py-2 bg-[#1a2535] text-white text-xs hover:bg-[#243347] transition-colors cursor-pointer whitespace-nowrap"
          >
            套用
          </button>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-500 mt-2 leading-relaxed">{error}</p>
      )}

      {/* Preview */}
      {value && (
        <div className="mt-3 flex items-start gap-3">
          <div className="w-20 h-20 border border-gray-100 flex-shrink-0 flex items-center justify-center overflow-hidden bg-gray-50">
            <img src={value} alt="preview" className="w-full h-full object-contain" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-gray-400 mb-1">圖片預覽</p>
            <p className="text-[10px] text-gray-500 break-all line-clamp-2">{value}</p>
            <button
              type="button"
              onClick={() => { onChange(''); setUrlInput(''); }}
              className="mt-1.5 text-[10px] text-red-400 hover:text-red-600 cursor-pointer whitespace-nowrap"
            >
              移除圖片
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
